// src/prototype/utils/initiativesImport.ts
// ============================================================
// EXISTING-INITIATIVES IMPORT — genuine CSV/XLSX parsing + status derivation
//
// The Existing Initiatives band promises a dedupe story ("compare, deduplicate, and enrich
// your current initiatives"). This module makes that real: it parses what the presenter
// actually drops and DERIVES the Status column rather than reading it from the file.
//
// Status is derived, never authored, because a template that ships its own "Matched" values
// proves nothing — the whole point of the beat is that the system recognised the overlap.
//   Matched   — the name fuzzy-matches a use case already in the demo library
//   Duplicate — the same initiative name appears earlier in the uploaded file
//   New       — neither
//
// PARSING NEVER THROWS AT THE UI. Every failure path returns `ok: false` and the caller
// falls back to the canonical rows, because an error state on the drop zone during a live
// demo is worse than a slightly-less-impressive success.
// ============================================================

import * as XLSX from 'xlsx';
import type { DemoInitiative, InitiativeStatus } from '../hooks/useDemoUploads';
import { DEMO_OPPORTUNITIES } from '../data/demoOpportunities';

/** Raw row shape before status derivation. New fields are optional so older callers and
 *  files without the columns keep working. */
export interface RawInitiativeRow {
  name: string;
  category: string;
  source: string;
  percentComplete?: number | null;
  committed?: boolean;
  ftesCommitted?: number | null;
  warnings?: string[];
}

/** The five canonical rows. Shipped in public/templates/D5_Existing_Initiatives_ACME_EXAMPLE.xlsx
 *  (and demo-assets/ACME_Existing_Initiatives.xlsx) and used as the graceful fallback.
 *  Deliberately produces 2 Matched / 1 Duplicate / 2 New, one committed library match
 *  (Predictive Prescription Analytics) and one Committed/percent mismatch warning. */
export const CANONICAL_INITIATIVE_ROWS: RawInitiativeRow[] = [
  // Matches "Care Coordination and Transition Management" (demo rank 1). Left uncommitted:
  // it is the lead evaluation story.
  { name: 'Care Coordination & Transition Management', category: 'Clinical Analytics', source: 'Internal Strategy Doc', percentComplete: null, committed: false },
  // Matches "Predictive Prescription Analytics" (demo rank 3). Committed: drives the
  // "{Client} Committed" filter in Evaluate.
  { name: 'Predictive Prescription Analytics', category: 'Pharmacy', source: 'CMO Priority List', percentComplete: 45, committed: true },
  // Genuinely absent from the library (verified against the current DEMO_OPPORTUNITIES titles
  // by librarySubset.test.ts's coverage, not just by eye — re-check this row if opportunity
  // titles change again).
  { name: 'Ambient Clinical Documentation Assistant', category: 'Operational Efficiency', source: 'Consulting Engagement', percentComplete: 30, committed: true },
  // Duplicate of row 1 — same initiative submitted twice by different teams. The realistic case.
  { name: 'Care Coordination & Transition Management', category: 'Clinical Analytics', source: 'Nursing Ops Submission', percentComplete: null, committed: false },
  // In progress but not marked committed: exercises the mismatch warning.
  { name: 'Nurse Turnover Prediction', category: 'Workforce', source: 'HR Analytics Team', percentComplete: 20, committed: false },
];

/** Normalised comparison key: case, punctuation and '&'/'and' folded away. */
function norm(s: string): string {
  return (s || '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/** Content words, minus filler that would otherwise inflate the overlap score. */
const STOP = new Set(['and', 'the', 'of', 'for', 'a', 'an', 'to', 'in', 'on', 'with', 'analytics', 'platform']);
function tokens(s: string): string[] {
  return norm(s).split(' ').filter((t) => t.length > 2 && !STOP.has(t));
}

/**
 * Fuzzy match against the demo library. Deliberately conservative — a false "Matched" on
 * screen is a worse failure than a missed one, because the presenter is claiming the system
 * recognised something.
 *
 * Matches when the normalised names are equal, or when one name's content words are a subset
 * of the other's (so "Care Coordination & Transition Management" matches the library's
 * "Care Coordination and Transition Management").
 */
export function matchLibraryName(name: string, libraryNames: string[]): string | null {
  const n = norm(name);
  if (!n) return null;
  const nt = tokens(name);
  if (nt.length === 0) return null;

  const hit = libraryNames.find((lib) => {
    const l = norm(lib);
    if (l === n) return true;
    const lt = tokens(lib);
    if (lt.length === 0) return false;
    const overlap = nt.filter((t) => lt.includes(t)).length;
    // every content word of the shorter name present in the longer one
    return overlap === Math.min(nt.length, lt.length) && overlap >= 2;
  });
  return hit ?? null;
}

export function matchesLibrary(name: string, libraryNames: string[]): boolean {
  return matchLibraryName(name, libraryNames) !== null;
}

/** Library names the matcher compares against (the demo opportunity titles). */
export function libraryNames(): string[] {
  return (DEMO_OPPORTUNITIES as { title: string }[]).map((o) => o.title);
}

/** Library title -> use case id, for carrying a row's Committed flag onto its use case. */
function opportunityIdForTitle(title: string): string | null {
  const hit = (DEMO_OPPORTUNITIES as { id: string; title: string }[]).find((o) => o.title === title);
  return hit ? hit.id : null;
}

/**
 * Derive Status for each row, in file order.
 * Duplicate takes precedence over Matched: a second occurrence is a duplicate first.
 * The first occurrence is the row the client keeps, so only it can carry Committed onto a
 * library use case (see committedByOpportunity).
 */
export function deriveStatuses(
  rows: RawInitiativeRow[],
  library: string[] = libraryNames(),
): DemoInitiative[] {
  const seen = new Set<string>();
  return rows.map((r) => {
    const key = norm(r.name);
    let status: InitiativeStatus;
    let matched: string | null = null;
    if (seen.has(key)) status = 'Duplicate';
    else {
      matched = matchLibraryName(r.name, library);
      status = matched ? 'Matched' : 'New';
    }
    seen.add(key);
    const percentComplete = r.percentComplete ?? null;
    const committed = r.committed ?? false;
    const warnings = [...(r.warnings ?? [])];
    if (!committed && percentComplete !== null && percentComplete > 0) {
      warnings.push('In progress but not marked Committed');
    }
    return {
      name: r.name,
      category: r.category,
      source: r.source,
      status,
      percentComplete,
      committed,
      ftesCommitted: r.ftesCommitted ?? null,
      matchedOpportunityId: matched ? opportunityIdForTitle(matched) : null,
      warnings,
    };
  });
}

/** Committed status per library use case, from the rows the client keeps (Matched rows).
 *  Uncommitted matches are included too so Evaluate can show Percent Complete. */
export interface UseCaseCommitment {
  committed: boolean;
  percentComplete: number | null;
  initiativeName: string;
}

export function commitmentsByOpportunity(
  rows: DemoInitiative[] | null | undefined,
): Map<string, UseCaseCommitment> {
  const out = new Map<string, UseCaseCommitment>();
  for (const r of rows ?? []) {
    if (r.status !== 'Matched' || !r.matchedOpportunityId) continue;
    if (out.has(r.matchedOpportunityId)) continue;
    out.set(r.matchedOpportunityId, {
      committed: Boolean(r.committed),
      percentComplete: r.percentComplete ?? null,
      initiativeName: r.name,
    });
  }
  return out;
}

/** The canonical set with statuses derived — the graceful fallback. */
export function canonicalInitiatives(): DemoInitiative[] {
  return deriveStatuses(CANONICAL_INITIATIVE_ROWS);
}

// ---- parsing ---------------------------------------------------------------

const HEADER_ALIASES: Record<string, string[]> = {
  name: ['initiative name', 'initiative', 'name', 'title'],
  category: ['category', 'domain', 'area'],
  source: ['source', 'origin', 'submitted by'],
  percentComplete: ['percent complete', 'pct complete', 'complete', 'progress', 'completion'],
  committed: ['committed', 'is committed', 'commitment'],
  ftesCommitted: ['ftes committed', 'fte committed', 'ftes', 'fte'],
};

/** Percent Complete: integer 0–100. Accepts "45%", "45", and Excel percent cells (0.45).
 *  Returns undefined for blank, or a rejection note for anything else. */
export function parsePercent(raw: unknown): { value: number | null; warning?: string } {
  const s = String(raw ?? '').trim();
  if (s === '') return { value: null };
  const hasPct = s.endsWith('%');
  const n = Number(s.replace('%', '').trim());
  if (!Number.isFinite(n)) return { value: null, warning: `Percent Complete "${s}" is not a number; ignored` };
  // A bare decimal in (0, 1) is an Excel percent-formatted cell (0.45 = 45%).
  const v = !hasPct && n > 0 && n < 1 && s.includes('.') ? n * 100 : n;
  const rounded = Math.round(v);
  if (rounded < 0 || rounded > 100) return { value: null, warning: `Percent Complete ${s} is outside 0–100; ignored` };
  return { value: rounded };
}

/** Committed: Y/N, Yes/No, True/False, 1/0. Blank = No. */
export function parseYesNo(raw: unknown): { value: boolean; warning?: string } {
  const s = String(raw ?? '').trim().toLowerCase();
  if (s === '') return { value: false };
  if (['y', 'yes', 'true', '1'].includes(s)) return { value: true };
  if (['n', 'no', 'false', '0'].includes(s)) return { value: false };
  return { value: false, warning: `Committed "${String(raw).trim()}" not recognised; treated as No` };
}

function parseFtes(raw: unknown): number | null {
  const s = String(raw ?? '').trim();
  if (s === '') return null;
  const n = Number(s);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

function pickColumn(headers: string[], field: keyof typeof HEADER_ALIASES): number {
  const wanted = HEADER_ALIASES[field];
  return headers.findIndex((h) => wanted.includes(norm(h)));
}

/** Minimal CSV reader: handles quoted fields and embedded commas. */
export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let inQuotes = false;
  for (let i = 0; i < text.length; i += 1) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') { cell += '"'; i += 1; } else inQuotes = false;
      } else cell += c;
    } else if (c === '"') inQuotes = true;
    else if (c === ',') { row.push(cell); cell = ''; }
    else if (c === '\n') { row.push(cell); rows.push(row); row = []; cell = ''; }
    else if (c !== '\r') cell += c;
  }
  if (cell !== '' || row.length > 0) { row.push(cell); rows.push(row); }
  return rows.filter((r) => r.some((v) => v.trim() !== ''));
}

export interface ParseResult {
  ok: boolean;
  rows: DemoInitiative[];
  /** Why the fallback was used. Surfaced quietly in the UI, never as an error. */
  note?: string;
}

/** Turn a matrix (header row + data rows) into initiatives, or fail. */
function fromMatrix(matrix: string[][]): ParseResult {
  if (matrix.length < 2) return { ok: false, rows: [], note: 'No data rows found' };
  const headers = matrix[0].map((h) => String(h ?? ''));
  const iName = pickColumn(headers, 'name');
  if (iName === -1) return { ok: false, rows: [], note: 'No "Initiative Name" column found' };
  const iCat = pickColumn(headers, 'category');
  const iSrc = pickColumn(headers, 'source');
  const iPct = pickColumn(headers, 'percentComplete');
  const iCom = pickColumn(headers, 'committed');
  const iFte = pickColumn(headers, 'ftesCommitted');

  const parsed: RawInitiativeRow[] = matrix.slice(1)
    .map((r) => {
      const pct = iPct === -1 ? { value: null } : parsePercent(r[iPct]);
      const com = iCom === -1 ? { value: false } : parseYesNo(r[iCom]);
      const warnings = [pct.warning, com.warning].filter((w): w is string => Boolean(w));
      return {
        name: String(r[iName] ?? '').trim(),
        category: iCat === -1 ? '—' : String(r[iCat] ?? '').trim() || '—',
        source: iSrc === -1 ? '—' : String(r[iSrc] ?? '').trim() || '—',
        percentComplete: pct.value,
        committed: com.value,
        ftesCommitted: iFte === -1 ? null : parseFtes(r[iFte]),
        warnings,
      };
    })
    .filter((r) => r.name !== '');

  if (parsed.length === 0) return { ok: false, rows: [], note: 'No named initiatives found' };
  return { ok: true, rows: deriveStatuses(parsed) };
}

/**
 * Parse a dropped file. NEVER throws — on any failure returns ok:false so the caller can
 * fall back to the canonical rows without an error state appearing on screen.
 */
export async function parseInitiativesFile(file: File): Promise<ParseResult> {
  try {
    const lower = (file.name || '').toLowerCase();
    if (lower.endsWith('.csv') || lower.endsWith('.txt') || file.type === 'text/csv') {
      const text = await file.text();
      return fromMatrix(parseCsv(text));
    }
    // XLSX / XLS via SheetJS (already a dependency).
    const buf = await file.arrayBuffer();
    const wb = XLSX.read(buf, { type: 'array' });
    const sheetName = wb.SheetNames[0];
    if (!sheetName) return { ok: false, rows: [], note: 'Workbook has no sheets' };
    const matrix = XLSX.utils.sheet_to_json<string[]>(wb.Sheets[sheetName], {
      header: 1, blankrows: false, defval: '',
    }) as unknown as string[][];
    return fromMatrix(matrix.map((r) => r.map((c) => String(c ?? ''))));
  } catch (err) {
    return { ok: false, rows: [], note: 'Could not read the file' };
  }
}
